// @flow
import React, { useEffect } from "react";
import { Segment, Form, Grid, Button, Icon as SemanticIcon, Divider } from "semantic-ui-react";
import { Field, reduxForm, getFormValues, type FormProps } from "redux-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Styles from "./Offer.module.scss";
import {
  InputField,
  ProvidersField,
  TariffsField,
} from "modules/ChangeService/ChangeServices/submodules/Old_Edit/components/Fields";
import { ConsumptionField } from "modules/ChangeService/ChangeServices/submodules/Old_Edit/components/ConsumptionField";
import { Location } from "modules/ChangeService/components/FormComponents";
import { IconOptions } from "../../../misc/components/IconOptions";
import { getStepRoute } from "modules/ChangeService/Wizard/misc/Routes";
import { fetchCategoryBySlug } from "redux/actions/category";
import { Loading } from "Commons/Components";
import { CategoryTypes } from "Commons/Modules/category";
import { isRequired } from "modules/ChangeService/ChangeServices/submodules/Old_Edit/misc/Validators";
import { isPostalCode } from "Commons/Utilities/validators";
import type { CategoryT } from "Commons/Modules/category";
import { FormLabelWithDivider as FormLabel } from "Commons/Components/FormComponents/components/FormLabelWithDivider";

import type { PathT, StepT } from "../../../misc/Types";
import { getPreviousNextStep } from "../../../misc/PathHelpers";

type Props = {|
  onNavigation: Function,
  steps: PathT,
  step: StepT,
  authenticated: boolean,
  categorySlug: string,
  category: CategoryT,
|} & FormProps;

function OfferStep(props: Props) {
  const { onNavigation, steps, step, authenticated, categorySlug, form, change, initialValues = {}, location } = props;

  const dispatch = useDispatch();

  useEffect(() => {
    if (categorySlug) {
      dispatch(fetchCategoryBySlug(categorySlug));
    }
  }, [categorySlug, dispatch]);

  const category = useSelector(({ category }) =>
    category.categories.find((category) => category.slug === categorySlug)
  );

  const categoryEndpoint = initialValues.category === CategoryTypes.GAS ? "gas" : "electricity";

  const categoryName = categorySlug === "gas" ? "Gas" : "Strom";

  const currentValues = useSelector((state) => getFormValues("ChangeService")(state));

  const { t } = useTranslation("changeService");
  const { nextStep } = getPreviousNextStep(steps, step.id);

  if (!category) return <Loading />;

  return (
    <Segment.Group style={{ zIndex: 1 }}>
      <Segment textAlign="center" className={Styles["header"]}>
        {t("mobile.offer.title")}
      </Segment>
      <Segment>
        <Form>
          <Grid stackable>
            <Grid.Column>
              <Form.Group>
                <FormLabel width={16} title={t("common:postalCode")} />
                <Field
                  name="postalcode"
                  component={InputField}
                  validate={[isRequired, isPostalCode]}
                  props={{ width: 16 }}
                />
              </Form.Group>
              <Form.Group>
                <FormLabel width={16} title={t("common:location")} />
                <Location
                  fields={{
                    postalCode: "postalcode",
                    locationId: "locationId",
                    locationMainName: "locationMainName",
                    locationName: "location",
                    locationPartName: "locationPartName",
                  }}
                  initialValues={initialValues}
                  category={categoryEndpoint}
                  form={form}
                  change={change}
                  width={16}
                />
              </Form.Group>
              <Form.Group>
                <FormLabel
                  width={16}
                  title={t("mobile.offer.consumption.title")}
                  subTitle={t("mobile.offer.consumption.subTitle", { categoryName })}
                  centered
                />
                <Field
                  name="currentConsumption"
                  component={ConsumptionField}
                  props={{ category: { categoryType: category.categoryType } }}
                />
              </Form.Group>
              <Form.Group>
                <FormLabel width={16} title={t("mobile.offer.ecoTariff.title")} centered />
                <Field
                  name="ecoEnergy"
                  component={IconOptions}
                  props={{
                    options: [
                      {
                        icon: "sun",
                        label: t("mobile.offer.ecoTariff.options.YES"),
                        value: true,
                      },
                      {
                        icon: "world",
                        label: t("mobile.offer.ecoTariff.options.NO"),
                        value: false,
                      },
                    ],
                  }}
                />
              </Form.Group>
              <Form.Group>
                <FormLabel width={16} title={t("mobile.offer.provider", { categoryName })} />
                <Field
                  name="categoryProviderId"
                  component={ProvidersField}
                  props={{
                    initialValues: {
                      postalcode: currentValues.postalcode,
                      locationId: currentValues.locationId,
                      currentConsumption: currentValues.currentConsumption,
                    },
                    category: {
                      categoryId: category.id,
                    },
                    showExtraLink: false,
                    mobileChangeService: true,
                    onChange: () => {},
                  }}
                />
              </Form.Group>
              <Form.Group>
                <FormLabel width={16} title={t("mobile.offer.tariff", { categoryName })} />
                <Field
                  name="currentTariff"
                  component={TariffsField}
                  props={{
                    width: 16,
                    categoryType: category.categoryType,
                    categoryProviderId: currentValues.categoryProviderId,
                    postalCode: currentValues.postalcode,
                    mobileChangeService: true,
                  }}
                />
              </Form.Group>
              <Divider hidden />
              <Grid columns="1" centered>
                <Grid.Row>
                  <Link
                    to={{
                      pathname: getStepRoute(authenticated, categorySlug, nextStep.route),
                      search: location.search,
                    }}
                  >
                    <Button primary size="big" onClick={onNavigation}>
                      {t("mobile.offer.continue")}
                      <SemanticIcon name="arrow right" />
                    </Button>
                  </Link>
                </Grid.Row>
              </Grid>
            </Grid.Column>
          </Grid>
        </Form>
      </Segment>
    </Segment.Group>
  );
}

const Offer = reduxForm({
  form: "ChangeService",
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(OfferStep);

export { Offer };
